// ============================================================================
// Ask AI's supply card — approve or reject one supply request (item #944, the
// supply part; TCSD-0531 one tool per part, saved through the screen's own
// function). The server half is supabase/functions/am-copilot/supply.ts.
// Every save here is the approvals screen's own (supply-management/approvals.ts).
// ============================================================================

import { approveSupplyRequest, rejectSupplyRequest } from '../supply-management/approvals';
import { rpcErrorMessage } from '../../shared/rpc-error';
import type { FieldSpec } from './building-actions';

export const SUPPLY_ACTIONS = ['approve', 'reject'] as const;
export type SupplyAction = (typeof SUPPLY_ACTIONS)[number];

/** A draft as the server persisted it (prepareSupplyAction). */
export interface SupplyDraft {
  request_id: string;
  action: SupplyAction;
  fields: Record<string, unknown>;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface SupplyCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  doItLabel: string;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const ctxOf = (d: SupplyDraft) => (d.context ?? {}) as Record<string, unknown>;

/** The request as the queue shows it: the item and quantity. */
export const supplyOf = (d: SupplyDraft) => str(ctxOf(d).item) || str(d.display_name) || d.request_id;

/** What the card shows for this draft, with the person's edits `f`. */
export function supplyCard(d: SupplyDraft, f: Record<string, unknown>): SupplyCardSpec {
  const c = ctxOf(d);
  const info = [
    { label: 'Building', value: str(c.building) || '—' },
    { label: 'Quantity', value: str(c.quantity) || '—' },
    { label: 'Urgency', value: str(c.urgency) || '—' },
    { label: 'Asked for by', value: str(c.requester) || '—' },
  ];
  if (d.action === 'approve') return { title: 'Approve', fields: [], info, doItLabel: 'Approve', missing: null };
  return {
    title: 'Reject',
    fields: [{ key: 'reason', label: 'Rejection reason', kind: 'textarea', required: true }],
    info,
    doItLabel: 'Reject',
    missing: str(f.reason).trim() ? null : 'A rejection reason, please.',
  };
}

export interface SupplyCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}

/** Save one supply draft through the approvals screen's own function. */
export async function commitSupplyAction(d: SupplyDraft, f: Record<string, unknown>): Promise<SupplyCommitResult> {
  const missing = supplyCard(d, f).missing;
  if (missing) return { ok: false, error: missing };
  try {
    if (d.action === 'approve') {
      await approveSupplyRequest(d.request_id);
      return { ok: true, note: `${supplyOf(d)} — approved.`, target_row_id: d.request_id, committed_payload: { status: 'Approved' } };
    }
    await rejectSupplyRequest(d.request_id, str(f.reason));
    return { ok: true, note: `${supplyOf(d)} — rejected.`, target_row_id: d.request_id, committed_payload: { status: 'Rejected', reason: str(f.reason).trim() } };
  } catch (e) {
    return { ok: false, error: rpcErrorMessage(e, 'That did not save.') };
  }
}
